import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';

import { alertService } from '../../../shared/services/alertService';
import {
  useLazyGetNewCounsellingQuery,
  usePostPlanCounsellingMutation,
  usePostVacancyUpdateMutation,
} from '../counselling.services';
import { editCounsellingTypes } from './planningOptions';
import { displayDate } from './dateUtils';
import { CounsellingListRow, Option, LANDING_COLUMNS } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface PlanningLandingProps {
  userType: string;
  userType1: string;
  allDirectorate: Option[];
  onAddNew: () => void;
  onEdit: (row: CounsellingListRow) => void;
}

type ActionKind = 'counselling' | 'archived' | 'setting';

/**
 * Landing list of counsellings with row actions (Angular GetAllNewCounselling
 * + stopStartBtn + handleConfirm + handleSetting). Active list + archived tab;
 * start/stop, archive, and a settings dialog with the status toggles.
 */
export const PlanningLanding = ({
  userType,
  userType1,
  allDirectorate,
  onAddNew,
  onEdit,
}: PlanningLandingProps) => {
  const navigate = useNavigate();
  const [triggerList] = useLazyGetNewCounsellingQuery();
  const [postPlanCounselling] = usePostPlanCounsellingMutation();
  const [postVacancyUpdate] = usePostVacancyUpdateMutation();

  const [activeList, setActiveList] = useState<CounsellingListRow[]>([]);
  const [archivedList, setArchivedList] = useState<CounsellingListRow[]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Per-column filter state (filterDisplay="row") — one CONTAINS matcher per
  // landing column, plus the global filter.
  const [filters, setFilters] = useState<DataTableFilterMeta>(() => {
    const init: DataTableFilterMeta = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };
    LANDING_COLUMNS.forEach((c) => {
      init[c.field] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    });
    return init;
  });

  // Confirm dialog.
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmHeading, setConfirmHeading] = useState('');
  const [confirmContent, setConfirmContent] = useState('');
  const [actionKind, setActionKind] = useState<ActionKind>('counselling');
  const [selectedRow, setSelectedRow] = useState<CounsellingListRow | null>(
    null
  );

  // Settings dialog.
  const [settingVisible, setSettingVisible] = useState(false);
  const [settingRow, setSettingRow] = useState<CounsellingListRow | null>(null);
  const [toggles, setToggles] = useState({
    ApplicationStatus: false,
    VacanyPublish: false,
    SeniorityPublish: false,
    VacancyChallenge: false,
    SeniorityChallenge: false,
    PreSelectVacancies: false,
    UpdateCandidate: false,
    VacUptSts: false,
    same_district_sts: false,
    willing_sts: false,
  });

  const typeLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    editCounsellingTypes().forEach((t) => (map[t.value] = t.label));
    return map;
  }, []);

  const directorateLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    allDirectorate.forEach((d) => (map[d.value] = d.label));
    return map;
  }, [allDirectorate]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await triggerList().unwrap();
      if (!res?.dataStatus) {
        alertService.error(res.message);
        return;
      }
      // Role-based directorate filter (Angular GetAllNewCounselling).
      let result: any[] = res.result ?? [];
      if (userType === '5' && userType1 === '29') {
        result = result.filter((x) => String(x.Directorate) === '4');
      } else if (userType === '5' && userType1 === '6') {
        result = result.filter((x) => String(x.Directorate) === '5');
      } else if (userType === '5' && userType1 === '8') {
        result = result.filter((x) => String(x.Directorate) === '6');
      } else {
        result = result.filter(
          (x) =>
            String(x.Directorate) !== '4' &&
            String(x.Directorate) !== '5' &&
            String(x.Directorate) !== '6'
        );
      }

      // Map type/directorate to labels + combined date; keep raw fields.
      const mapped = result.map((r) => ({
        ...r,
        CounsellingType:
          typeLabelById[String(r.CounsellingType)] ?? r.CounsellingType,
        Directorate:
          directorateLabelById[String(r.Directorate)] ?? r.Directorate,
        FromDate: `${displayDate(r.FromDate)} - ${displayDate(r.ToDate)}`,
      }));

      setActiveList(mapped.filter((r) => String(r.Status) !== '3'));
      setArchivedList(mapped.filter((r) => String(r.Status) === '3'));
    } catch {
      alertService.error('Failed to load counselling list');
    } finally {
      setLoading(false);
    }
  }, [triggerList, userType, userType1, typeLabelById, directorateLabelById]);

  useEffect(() => {
    // Fire-and-forget async load; setState happens after the await, not
    // synchronously in the effect body.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadList();
  }, [loadList]);

  // ── Row actions ─────────────────────────────────────────────────────────────
  const stopStartBtn = (row: CounsellingListRow, kind: ActionKind) => {
    setSelectedRow(row);
    setActionKind(kind);
    if (kind === 'counselling') {
      const status = String(row.Status);
      if (status === '0' || status === '2') {
        setConfirmHeading('Start Counselling');
        setConfirmContent('Are you sure you want to start this counselling ?');
      } else {
        setConfirmHeading('Stop Counselling');
        setConfirmContent('Are you sure you want to stop this counselling ?');
      }
    } else if (kind === 'archived') {
      setConfirmHeading('Archived');
      setConfirmContent(
        'Are you sure you want to move to archived this counselling ?'
      );
    }
    setConfirmVisible(true);
  };

  const handleSetting = (row: CounsellingListRow) => {
    setSettingRow(row);
    setToggles({
      ApplicationStatus: row.AppSts === '1',
      VacanyPublish: row.VacanyPublish === '1',
      SeniorityPublish: row.SeniorityPublish === '1',
      VacancyChallenge: row.VacancyChallenge === '1',
      SeniorityChallenge: row.SeniorityChallenge === '1',
      PreSelectVacancies: row.PreSelectVacancies === '1',
      UpdateCandidate: row.UpdateCandidate === '1',
      VacUptSts: row.VacUptSts === '1',
      same_district_sts: row.same_district_sts === '1',
      willing_sts: row.willing_sts === '1',
    });
    setSettingVisible(true);
  };

  const yn = (b: boolean) => (b ? '1' : '2');

  const handleConfirm = async (kind: ActionKind = actionKind) => {
    if (kind !== 'setting' && !selectedRow) return;
    let records: any;
    if (kind === 'archived') {
      records = { IndexId: selectedRow?.IndexId, Status: '3' };
    } else if (kind === 'setting') {
      records = {
        IndexId: settingRow?.IndexId,
        AppSts: yn(toggles.ApplicationStatus),
        VacanyPublish: yn(toggles.VacanyPublish),
        SeniorityPublish: yn(toggles.SeniorityPublish),
        VacancyChallenge: yn(toggles.VacancyChallenge),
        SeniorityChallenge: yn(toggles.SeniorityChallenge),
        PreSelectVacancies: yn(toggles.PreSelectVacancies),
        UpdateCandidate: yn(toggles.UpdateCandidate),
        same_district_sts: yn(toggles.same_district_sts),
        willing_sts: yn(toggles.willing_sts),
      };
      // Separate vacancy-update-status post (Angular desReq).
      try {
        await postVacancyUpdate({
          records: {
            Des: settingRow?.Designation,
            VacUptSts: yn(toggles.VacUptSts),
          },
        }).unwrap();
      } catch {
        /* non-fatal */
      }
    } else {
      // counselling start/stop
      const status = String(selectedRow?.Status);
      const next = status === '0' || status === '2' ? '1' : '2';
      records = { IndexId: selectedRow?.IndexId, Status: next };
    }

    try {
      const res: any = await postPlanCounselling({ records }).unwrap();
      if (res?.dataStatus) {
        alertService.success(
          confirmHeading === 'Start Counselling'
            ? 'Counselling started sucessfully'
            : confirmHeading === 'Stop Counselling'
              ? 'Counselling stopped sucessfully'
              : (res.message ?? 'Updated successfully')
        );
        loadList();
        setConfirmVisible(false);
        setSettingVisible(false);
      } else {
        alertService.error('Something went wrong. Please try again');
      }
    } catch {
      alertService.error('Something went wrong. Please try again');
    }
  };

  // Pass the IndexId via router state (clean URL), matching how
  // OrderCopyDetails reads it from location.state.path.
  const getOrderCopy = (row: CounsellingListRow) =>
    navigate('/counselling/order-copy-details', {
      state: { path: row.IndexId },
    });
  const isMutual = (row: CounsellingListRow) =>
    row.CounsellingType === 'Mutual Transfer';

  // ── Column templates ────────────────────────────────────────────────────────
  const snoBody = (row: CounsellingListRow, opts: any) => (
    <span>
      <b>{opts.rowIndex + 1}</b> - ({row.IndexId})
    </span>
  );

  const orderCopyBody = (row: CounsellingListRow) => (
    <Button
      type="button"
      label="Order&nbsp;Copy"
      className="p-button-link p-0"
      onClick={() => getOrderCopy(row)}
    />
  );

  // Action + Settings buttons rendered together on a single row.
  const actionBody = (row: CounsellingListRow) => {
    const started = String(row.Status) === '1';
    return (
      <div className="d-flex align-items-center gap-2">
        {!started && (
          <Button
            type="button"
            className="p-button-primary"
            icon="pi pi-pencil"
            onClick={() => onEdit(row)}
          />
        )}
        <Button
          type="button"
          className="p-button-warning"
          icon="pi pi-arrow-circle-down"
          onClick={() => stopStartBtn(row, 'archived')}
        />
        <Button
          type="button"
          icon={started ? 'pi pi-pause' : 'pi pi-play'}
          className={started ? 'p-button-danger' : 'p-button-success'}
          onClick={() => stopStartBtn(row, 'counselling')}
        />
        <Button
          type="button"
          className="p-button-secondary"
          icon="pi pi-cog"
          onClick={() => handleSetting(row)}
        />
      </div>
    );
  };

  const toggleRow = (label: string, key: keyof typeof toggles) => (
    <>
      <div className="col-sm-6 col-md-6 col-lg-3 col-xl-3">
        <label>{label}</label>
      </div>
      <div className="col-sm-6 col-md-6 col-lg-3 col-xl-3">
        <InputSwitch
          checked={toggles[key]}
          onChange={(e) => setToggles((t) => ({ ...t, [key]: !!e.value }))}
        />
      </div>
    </>
  );

  const showFullToggles = !settingRow || !isMutual(settingRow);

  return (
    <div className="main-body-content plan-new-counselling">
      <TabView>
        <TabPanel header="Plan New Counselling">
          <div className="Counselling-Secondary">
            <DataTable
              value={activeList}
              loading={loading}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              rowHover
              stripedRows
              responsiveLayout="scroll"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              emptyMessage="No Records Found"
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => setFilters(e.filters)}
              globalFilterFields={LANDING_COLUMNS.map((c) => c.field)}
              header={
                <div className="row">
                  <div className="col-md-6 col-12">
                    <InputText
                      type="text"
                      placeholder="Search.."
                      value={globalFilter}
                      onChange={(e) => {
                        const value = e.target.value;
                        setGlobalFilter(value);
                        setFilters((prev) => ({
                          ...prev,
                          global: {
                            value,
                            matchMode: FilterMatchMode.CONTAINS,
                          },
                        }));
                      }}
                    />
                  </div>
                  <div className="col-md-6 col-12 text-end">
                    <Button
                      type="button"
                      label="Add New Counselling"
                      onClick={onAddNew}
                    />
                  </div>
                </div>
              }
            >
              <Column
                header="S.No - (ID)"
                body={snoBody}
                style={{ width: '9rem', whiteSpace: 'nowrap' }}
              />
              {LANDING_COLUMNS.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable
                  filter
                  filterField={col.field}
                  filterPlaceholder="Search"
                  showFilterMenu={false}
                />
              ))}
              <Column header="Order Copy" body={orderCopyBody} />
              <Column header="Action" body={actionBody} />
            </DataTable>
          </div>
        </TabPanel>

        <TabPanel header="Archived Counselling">
          <div className="Counselling-Secondary">
            <DataTable
              value={archivedList}
              loading={loading}
              paginator
              stripedRows
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              rowHover
              responsiveLayout="scroll"
              emptyMessage="No Records Found"
            >
              <Column
                header="S.No"
                body={(_r, o) => o.rowIndex + 1}
                style={{ width: '5rem' }}
              />
              {LANDING_COLUMNS.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable
                />
              ))}
            </DataTable>
          </div>
        </TabPanel>
      </TabView>

      {/* Start/Stop/Archive confirm dialog */}
      <Dialog
        header={confirmHeading}
        modal
        visible={confirmVisible}
        closable={false}
        draggable={false}
        headerClassName="dialog-header-secondary"
        onHide={() => setConfirmVisible(false)}
        style={{ width: '40vw' }}
      >
        <div className="row mt-4">
          <div className="col">
            <label>{confirmContent}</label>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12 text-end">
            <Button
              type="button"
              label="Yes"
              className="p-button-success"
              onClick={() => handleConfirm()}
            />
            &nbsp;
            <Button
              type="button"
              label="No"
              className="p-button-danger"
              onClick={() => {
                setConfirmVisible(false);
                loadList();
              }}
            />
          </div>
        </div>
      </Dialog>

      {/* Settings dialog */}
      <Dialog
        header="Settings"
        modal
        visible={settingVisible}
        draggable={false}
        headerClassName="dialog-header-secondary"
        onHide={() => setSettingVisible(false)}
        style={{ width: '60vw' }}
      >
        <div className="row mt-4 pt-2">
          {toggleRow('Application Status', 'ApplicationStatus')}
          {showFullToggles && toggleRow('Vacancy Publish', 'VacanyPublish')}
        </div>
        {showFullToggles && (
          <>
            <div className="row mt-4">
              {toggleRow('Seniority Publish', 'SeniorityPublish')}
              {toggleRow('Vacancies Challenge', 'VacancyChallenge')}
            </div>
            <div className="row mt-4">
              {toggleRow('Seniority Challenge', 'SeniorityChallenge')}
              {toggleRow('Pre-select Vacancies', 'PreSelectVacancies')}
            </div>
            <div className="row mt-4">
              {toggleRow('Update Candidate', 'UpdateCandidate')}
              {toggleRow('Update Vacancy', 'VacUptSts')}
            </div>
            <div className="row mt-4">
              {toggleRow('Same District', 'same_district_sts')}
              {toggleRow('Is Willing', 'willing_sts')}
            </div>
          </>
        )}
        <div className="row mt-4 pt-2">
          <div className="col-12 text-end">
            <Button
              type="button"
              label="Submit"
              className="p-button-success"
              onClick={() => handleConfirm('setting')}
            />
            &nbsp;
            <Button
              type="button"
              label="Cancel"
              className="p-button-danger"
              onClick={() => setSettingVisible(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
